import type {ScanContext} from "../scan-model/ScanContext.tsx";
import {
    type OverallTaskHistoryResult,
    type ProjectTaskBatchesResult,
    type SingleTaskBatchScanResult
} from "../scan-model/GroupedTaskBatchesScanResult.tsx";
import type {AnalysisOptionTaskHistory} from "../../2-option-selection/AnalysisOptions.ts";
import type {MetlApiBase} from "../../../metl-api/MetlApiBase.ts";
import type {ProjectId} from "../../../metl-models/id/ProjectId.tsx";
import type {ScannedVersionJobsResult} from "./3-ScanJobs.ts";
import {calculateProjectTaskAnalysis} from "./task/ProjectTaskCalculator.tsx";
import {calculateOverallTaskResults} from "./task/OverallTaskCalculator.tsx";
import type {TransformationJob} from "../../../metl-models/jobs/Transformation.ts";
import type {OrchestrationJob} from "../../../metl-models/jobs/Orchestration.ts";
import {runPromises} from "../../../util/promises/PromiseRunner.tsx";
import {calculateSingleBatchAnalysis} from "./task/SingleBatchCalculator.tsx";
import type {VersionsScanPlan} from "./2-ScanVersions.tsx";

function generateTimeSplits(options: AnalysisOptionTaskHistory): [start: Date, end: Date][] {
    const {durationToQueryInDays, taskQueryDurationHours} = options
    // generate a series of start-end regions that cover the durationToQueryInDays, with each region being taskQueryDurationHours long, where the end time is 1s before the start time of the next region
    const now = new Date()
    const absoluteStart = new Date(now.getTime() - durationToQueryInDays * 24 * 60 * 60 * 1000)
    const numberOfRegions = Math.ceil((durationToQueryInDays * 24) / taskQueryDurationHours)
    const splits: [start: Date, end: Date][] = []
    for(let i = 0; i < numberOfRegions; i++) {
        const start = absoluteStart.getTime() + (i * taskQueryDurationHours * 60 * 60 * 1000)
        const end = start + (taskQueryDurationHours * 60 * 60 * 1000) - 1
        splits.push([
            new Date(start),
            new Date(end)
        ])
    }
    return splits
}

/**
 * Derive the unique projects to scan task history for from the version scan plan
 * built earlier in 2-ScanVersions. This avoids re-listing groups/projects against
 * METL (which is a frequent failure point) and keeps task history aligned with
 * the versions actually being analysed.
 */
function identifyProjectsFromPlan(versionsScanPlan: VersionsScanPlan): ProjectId[] {
    const seen = new Set<string>()
    const projects: ProjectId[] = []
    for (const version of versionsScanPlan.versions) {
        const key = `${version.id.groupName}/${version.id.projectName}`
        if (seen.has(key)) continue
        seen.add(key)
        projects.push({
            groupName: version.id.groupName,
            projectName: version.id.projectName
        })
    }
    return projects
}

type X = [ProjectTaskBatchesResult, individualAnalyses: SingleTaskBatchScanResult[]]

async function scanProjectTaskHistory(ctx: ScanContext, api: MetlApiBase, projectId: ProjectId, splits: [start: Date, end: Date][], jobs: ScannedVersionJobsResult[]): Promise<X | undefined> {
    const {logError, startAction, options} = ctx
    const {reportOne, complete} = startAction(`Scanning task history of project ${projectId.groupName}/${projectId.projectName}`, splits.length)
    const taskApi = api.groups.get(projectId.groupName).projects.get(projectId.projectName).taskHistory()
    const versionJobMap: Record<string, Record<string, TransformationJob | OrchestrationJob>> = {}
    for(const jobBlock of jobs) {
        if(jobBlock.versionId.groupName != projectId.groupName || jobBlock.versionId.projectName != projectId.projectName) continue
        const versionJobs = versionJobMap[jobBlock.versionId.versionName] ?? {}
        for(const job of jobBlock.jobs) {
            versionJobs[job.jobName] = job.debug
        }
        versionJobMap[jobBlock.versionId.versionName] = versionJobs
    }
    const runnerOpts = options.analyseTaskHistory?.promiseRunner
    const taskGroups = await runPromises(runnerOpts, splits, async ([start, end]) => {
        try {
            const batches = await taskApi.queryBetween(start, end)
            return batches.map(batch => calculateSingleBatchAnalysis(versionJobMap[batch.versionName], batch))
        } catch(e) {
            console.error("Failed to scan task history split", projectId, [start, end], e)
            logError(`Failed to scan task history split ${projectId.groupName}/${projectId.projectName} [${start.toISOString()} - ${end.toISOString()}]`, e)
            return []
        } finally {
            reportOne()
        }
    })
    complete()
    const tasks = taskGroups.flat()

    return calculateProjectTaskAnalysis(projectId, tasks)
}

export async function scanTaskHistory(ctx: ScanContext,
                                      versionsScanPlan: VersionsScanPlan,
                                      jobs: ScannedVersionJobsResult[]): Promise<OverallTaskHistoryResult | undefined> {
    const { log, logError, options, startAction } = ctx
    if(options.analyseTaskHistory && options.analyseTaskHistory.enabled) {
        log("Scanning task history")
        const {analyseTaskHistory} = options
        const projects = identifyProjectsFromPlan(versionsScanPlan)
        if (projects.length === 0) {
            log("No projects available from the version scan plan, skipping task history scan.")
            return undefined
        }
        log(`Identified ${projects.length} projects to scan from version plan. [${projects.map(project => `${project.groupName}/${project.projectName}`).join(", ")}]`)
        const splits = generateTimeSplits(analyseTaskHistory)
        log(`Generated ${splits.length} time splits`)
        const results: X[] = []
        const {reportOne, complete} = startAction("Scanning projects task history", projects.length)
        for(const project of projects) {
            try {
                log(`Starting scan of project ${project.groupName}/${project.projectName}`)
                const result = await scanProjectTaskHistory(ctx, ctx.api, project, splits, jobs)
                log(`Scanned task history of project ${project.groupName}/${project.projectName}`)
                if(result)
                    results.push(result)

            } catch(e) {
                logError(`Failed to scan task history of project ${project.groupName}/${project.projectName}`, e)
            } finally {
                reportOne()
            }
        }
        complete()
        log("Scanned task histories")

        if(results.length > 0)
            return calculateOverallTaskResults(results)

    }
    return undefined
}